import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, ArrowLeft } from "lucide-react";
import { formatMatricula, numRef } from "@/lib/constants";
import { toast } from "sonner";
import { VeiculoSugestao } from "@/components/VeiculoSugestao";

const emptyLine = () => ({ qtd: 1, descricao: "", ref_marca: "", marca: "", preco_unit: "", desconto: 0 });
const DRAFT_KEY = "atlas_rascunho_pedido";

export default function NovoPedido() {
  const navigate = useNavigate();
  const [clientes, setClientes] = useState([]);
  const [clienteId, setClienteId] = useState("");
  const [novoCliente, setNovoCliente] = useState({ nome: "", tipo: "oficina", telefone: "", nif: "" });
  const [modo, setModo] = useState("existente");
  const [veiculo, setVeiculo] = useState({ matricula: "", vin: "", marca: "", modelo: "", motorizacao: "" });
  const [prioridade, setPrioridade] = useState("3");
  const [descricao, setDescricao] = useState("");
  const [linhas, setLinhas] = useState([emptyLine()]);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => { api.get("/clientes").then((r) => setClientes(r.data)); }, []);

  // recuperar rascunho ao entrar
  useEffect(() => {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw) {
        const d = JSON.parse(raw);
        if (d.clienteId) setClienteId(d.clienteId);
        if (d.novoCliente) setNovoCliente(d.novoCliente);
        if (d.modo) setModo(d.modo);
        if (d.veiculo) setVeiculo(d.veiculo);
        if (d.prioridade) setPrioridade(d.prioridade);
        if (d.descricao) setDescricao(d.descricao);
        if (d.linhas?.length) setLinhas(d.linhas);
        const temConteudo = d.descricao || d.veiculo?.matricula || d.clienteId || d.novoCliente?.nome || (d.linhas || []).some((l) => l.descricao);
        if (temConteudo) toast.info("Rascunho recuperado — continuámos de onde parou.");
      }
    } catch { /* ignore */ }
    setLoaded(true);
  }, []);

  // gravar rascunho automaticamente a cada alteração
  useEffect(() => {
    if (!loaded) return;
    const temConteudo = descricao || veiculo.matricula || veiculo.marca || clienteId || novoCliente.nome || linhas.some((l) => l.descricao);
    try {
      if (temConteudo) {
        localStorage.setItem(DRAFT_KEY, JSON.stringify({ clienteId, novoCliente, modo, veiculo, prioridade, descricao, linhas }));
      } else {
        localStorage.removeItem(DRAFT_KEY);
      }
    } catch { /* ignore */ }
  }, [loaded, clienteId, novoCliente, modo, veiculo, prioridade, descricao, linhas]);

  const limparRascunho = () => {
    localStorage.removeItem(DRAFT_KEY);
    setClienteId(""); setNovoCliente({ nome: "", tipo: "oficina", telefone: "", nif: "" }); setModo("existente");
    setVeiculo({ matricula: "", vin: "", marca: "", modelo: "", motorizacao: "" }); setPrioridade("3");
    setDescricao(""); setLinhas([emptyLine()]);
    toast.success("Rascunho limpo.");
  };

  const usarVeiculo = (v, cliente) => {
    setVeiculo({ matricula: v.matricula || veiculo.matricula, vin: v.vin || veiculo.vin || "", marca: v.marca || "", modelo: v.modelo || "", motorizacao: v.motorizacao || "" });
    if (cliente) {
      setModo("existente"); setClienteId(cliente.id);
      if (!clientes.some((c) => c.id === cliente.id)) setClientes((cs) => [...cs, { ...cliente, tipo: "" }]);
    }
    toast.success(cliente ? `Veículo e cliente ${cliente.nome} preenchidos.` : "Dados do veículo preenchidos.");
  };

  const setLine = (i, k, v) => setLinhas((ls) => ls.map((l, idx) => (idx === i ? { ...l, [k]: v } : l)));
  const addLine = () => setLinhas((ls) => [...ls, emptyLine()]);
  const rmLine = (i) => setLinhas((ls) => ls.filter((_, idx) => idx !== i));

  const submit = async () => {
    setSaving(true);
    try {
      let cid = clienteId;
      if (modo === "novo") {
        if (!novoCliente.nome) { toast.error("Indique o nome do cliente."); setSaving(false); return; }
        const { data } = await api.post("/clientes", novoCliente);
        cid = data.id;
      }
      let vid = null;
      if (veiculo.matricula || veiculo.marca || veiculo.vin) {
        const { data } = await api.post("/veiculos", veiculo);
        vid = data.id;
      }
      const payload = {
        cliente_id: cid || null,
        veiculo_id: vid,
        prioridade: parseInt(prioridade),
        descricao_inicial: descricao,
        linhas: linhas
          .filter((l) => l.descricao)
          .map((l) => ({
            ...l,
            qtd: parseFloat(l.qtd) || 1,
            preco_unit: l.preco_unit === "" ? null : parseFloat(l.preco_unit),
            desconto: parseFloat(l.desconto) || 0,
          })),
      };
      const { data } = await api.post("/atendimentos", payload);
      localStorage.removeItem(DRAFT_KEY);
      toast.success(`Pedido ${numRef(data.numero, data.codigo)} criado.`);
      navigate(`/pedido/${data.id}`);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-3" data-testid="btn-voltar">
        <ArrowLeft size={15} /> Voltar
      </button>
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden fade-up">
        <div className="bg-sky-50 border-b border-sky-100 px-5 py-3">
          <div className="text-[10px] font-bold uppercase tracking-wider text-sky-700">Novo pedido de cotação</div>
          <div className="text-lg font-bold text-slate-900">Registar pedido</div>
        </div>
        <div className="p-5 space-y-5">
          {/* Cliente */}
          <div>
            <Label className="text-xs font-semibold text-slate-700">Cliente</Label>
            <div className="flex gap-2 mt-1 mb-2">
              <button onClick={() => setModo("existente")} className={`text-xs px-3 py-1 rounded-md border ${modo === "existente" ? "bg-sky-600 text-white border-sky-600" : "bg-white border-slate-200"}`} data-testid="modo-cliente-existente">Existente</button>
              <button onClick={() => setModo("novo")} className={`text-xs px-3 py-1 rounded-md border ${modo === "novo" ? "bg-sky-600 text-white border-sky-600" : "bg-white border-slate-200"}`} data-testid="modo-cliente-novo">Novo</button>
            </div>
            {modo === "existente" ? (
              <Select value={clienteId} onValueChange={setClienteId}>
                <SelectTrigger data-testid="select-cliente" className="h-9"><SelectValue placeholder="Selecionar cliente…" /></SelectTrigger>
                <SelectContent>
                  {clientes.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome} · {c.tipo}</SelectItem>)}
                </SelectContent>
              </Select>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="Nome" value={novoCliente.nome} onChange={(e) => setNovoCliente({ ...novoCliente, nome: e.target.value })} data-testid="input-novo-cliente-nome" className="h-9" />
                <Select value={novoCliente.tipo} onValueChange={(v) => setNovoCliente({ ...novoCliente, tipo: v })}>
                  <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="oficina">Oficina</SelectItem>
                    <SelectItem value="particular">Particular</SelectItem>
                  </SelectContent>
                </Select>
                <Input placeholder="Telefone" value={novoCliente.telefone} onChange={(e) => setNovoCliente({ ...novoCliente, telefone: e.target.value })} className="h-9" />
                <Input placeholder="NIF" value={novoCliente.nif} onChange={(e) => setNovoCliente({ ...novoCliente, nif: e.target.value })} className="h-9" />
              </div>
            )}
          </div>

          {/* Veiculo */}
          <div>
            <Label className="text-xs font-semibold text-slate-700">Veículo</Label>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mt-1">
              <Input placeholder="AA-00-00" maxLength={8} value={veiculo.matricula} onChange={(e) => setVeiculo({ ...veiculo, matricula: formatMatricula(e.target.value) })} data-testid="input-matricula" className="h-9 font-mono uppercase" />
              <Input placeholder="Marca" value={veiculo.marca} onChange={(e) => setVeiculo({ ...veiculo, marca: e.target.value.toUpperCase() })} className="h-9 uppercase" />
              <Input placeholder="Modelo" value={veiculo.modelo} onChange={(e) => setVeiculo({ ...veiculo, modelo: e.target.value.toUpperCase() })} className="h-9 uppercase" />
              <Input placeholder="Motorização" value={veiculo.motorizacao} onChange={(e) => setVeiculo({ ...veiculo, motorizacao: e.target.value })} className="h-9" />
              <Input placeholder="VIN / Nº chassi" value={veiculo.vin || ""} onChange={(e) => setVeiculo({ ...veiculo, vin: e.target.value.toUpperCase().replace(/\s/g, "").slice(0, 17) })} data-testid="input-vin" className="h-9 font-mono uppercase" />
            </div>
            <VeiculoSugestao matricula={veiculo.matricula} onUsar={usarVeiculo} />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs font-semibold text-slate-700">Prioridade</Label>
              <Select value={prioridade} onValueChange={setPrioridade}>
                <SelectTrigger className="h-9 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">P1 · Alta</SelectItem>
                  <SelectItem value="2">P2 · Média</SelectItem>
                  <SelectItem value="3">P3 · Normal</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label className="text-xs font-semibold text-slate-700">Descrição inicial</Label>
              <Input value={descricao} onChange={(e) => setDescricao(e.target.value)} placeholder="Ex.: revisão + carroçaria" className="h-9 mt-1" data-testid="input-descricao" />
            </div>
          </div>

          {/* Linhas */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs font-semibold text-slate-700">Linhas / peças</Label>
              <Button size="sm" variant="outline" onClick={addLine} data-testid="btn-add-linha" className="h-7 gap-1 text-xs"><Plus size={13} /> Linha</Button>
            </div>
            <div className="space-y-2">
              {linhas.map((l, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center">
                  <Input className="col-span-1 h-9" type="number" value={l.qtd} onChange={(e) => setLine(i, "qtd", e.target.value)} placeholder="Qtd" />
                  <Input className="col-span-4 h-9 uppercase" value={l.descricao} onChange={(e) => setLine(i, "descricao", e.target.value.toUpperCase())} placeholder="Descrição da peça" data-testid={`linha-descricao-${i}`} />
                  <Input className="col-span-2 h-9 font-mono uppercase" value={l.ref_marca} onChange={(e) => setLine(i, "ref_marca", e.target.value.replace(/\s+/g, "").toUpperCase())} placeholder="Ref. marca" data-testid={`linha-ref-${i}`} />
                  <Input className="col-span-2 h-9" type="number" value={l.preco_unit} onChange={(e) => setLine(i, "preco_unit", e.target.value)} placeholder="P. unit €" />
                  <Input className="col-span-2 h-9" type="number" value={l.desconto} onChange={(e) => setLine(i, "desconto", e.target.value)} placeholder="Desc %" />
                  <button className="col-span-1 text-rose-500 hover:text-rose-700 flex justify-center" onClick={() => rmLine(i)} data-testid={`btn-rm-linha-${i}`}><Trash2 size={15} /></button>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-between items-center gap-2 pt-2 border-t border-slate-100">
            <span className="text-[11px] text-slate-400 flex items-center gap-1" data-testid="rascunho-hint">
              Rascunho guardado automaticamente neste dispositivo
            </span>
            <div className="flex gap-2">
              <Button variant="ghost" className="text-slate-500" onClick={limparRascunho} data-testid="btn-limpar-rascunho">Limpar rascunho</Button>
              <Button variant="outline" onClick={() => navigate(-1)}>Cancelar</Button>
              <Button className="bg-sky-600 hover:bg-sky-700" onClick={submit} disabled={saving} data-testid="btn-guardar-pedido">
                {saving ? "A guardar…" : "Criar pedido"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
